import { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useSandboxSubmitMatch } from '@/services/mutations/sandbox';
import {
	SandboxField,
	SandboxModal,
	SandboxNotice,
	sandboxSelectClass,
} from './SandboxModal';
import { fullName } from '../sandboxLabels';
import { cn } from '@/lib/utils';

interface Props {
	scenarioId: string;
	catalog: Sandbox.Catalog;
	users: Sandbox.ScenarioUser[];
	initialUserId?: string | null;
	isOpen: boolean;
	onClose: () => void;
}

/** Responde o match como um usuário do cenário usando uma persona pronta. */
const SandboxMatchModal = ({
	scenarioId,
	catalog,
	users,
	initialUserId,
	isOpen,
	onClose,
}: Props) => {
	const eligibleUsers = useMemo(
		() => users.filter((u) => !u.match_answered),
		[users],
	);
	const [userId, setUserId] = useState('');
	const [persona, setPersona] = useState('');
	const [result, setResult] = useState<Match.Result | null>(null);

	useEffect(() => {
		if (!isOpen) return;
		setUserId(
			initialUserId && eligibleUsers.some((u) => u.id === initialUserId)
				? initialUserId
				: eligibleUsers[0]?.id ?? '',
		);
		setPersona(catalog.personas[0]?.key ?? '');
		setResult(null);
	}, [isOpen, initialUserId, eligibleUsers, catalog.personas]);

	const { mutate, isPending } = useSandboxSubmitMatch(scenarioId, setResult);

	const activePersona = catalog.personas.find((p) => p.key === persona);

	return (
		<SandboxModal
			isOpen={isOpen}
			onClose={onClose}
			title="Responder o match"
			description="Grava a resposta como o usuário faria em /match (uma por conta) e devolve o ranking real."
			icon={<Sparkles className="h-4 w-4" />}
			confirmText={result ? undefined : 'Responder'}
			onConfirm={
				result ? undefined : () => mutate({ user_id: userId, persona })
			}
			confirmDisabled={!userId || !persona || !catalog.match_form}
			isPending={isPending}
		>
			<div className="space-y-4 py-2">
				{!catalog.match_form ? (
					<SandboxNotice tone="warning">
						Não há formulário de match ativo. Ative em Match.
					</SandboxNotice>
				) : null}
				{eligibleUsers.length === 0 ? (
					<SandboxNotice tone="warning">
						Todos os usuários do cenário já responderam o match.
					</SandboxNotice>
				) : null}

				{result ? (
					<div className="space-y-3">
						<SandboxNotice
							tone={result.fallback ? 'warning' : 'success'}
						>
							{result.fallback
								? 'Resultado em fallback: a ordem veio do admin (pouca informação ou nenhum perfil preenchido).'
								: `Resultado calculado (confiança ${Math.round(
										(result.confidence ?? 0) * 100,
								  )}%).`}
						</SandboxNotice>
						<ul className="space-y-2">
							{result.professionals.map((p) => (
								<li
									key={p.id}
									className="flex items-center justify-between gap-3 rounded-xl border border-stone-100 px-3 py-2"
								>
									<span className="text-sm font-bold text-stone-900">
										{p.rank}º {fullName(p)}
									</span>
									{p.match_score != null ? (
										<span className="text-sm font-black text-stone-900">
											{p.match_score}%
										</span>
									) : null}
								</li>
							))}
							{result.professionals.length === 0 ? (
								<li className="text-xs text-stone-400">
									Nenhum profissional elegível.
								</li>
							) : null}
						</ul>
					</div>
				) : (
					<>
						<SandboxField label="Usuário">
							<select
								className={sandboxSelectClass}
								value={userId}
								onChange={(e) => setUserId(e.target.value)}
							>
								{eligibleUsers.map((u) => (
									<option key={u.id} value={u.id}>
										{fullName(u)} · {u.email}
									</option>
								))}
							</select>
						</SandboxField>
						<SandboxField label="Persona (como ele responderia)">
							<div className="flex flex-wrap gap-2">
								{catalog.personas.map((p) => (
									<button
										key={p.key}
										type="button"
										onClick={() => setPersona(p.key)}
										className={cn(
											'rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
											persona === p.key
												? 'bg-stone-900 text-white'
												: 'bg-stone-100 text-stone-600 hover:bg-stone-200',
										)}
									>
										{p.label}
									</button>
								))}
							</div>
						</SandboxField>
						{activePersona ? (
							<p className="text-xs text-stone-400">
								{activePersona.description}
							</p>
						) : null}
					</>
				)}
			</div>
		</SandboxModal>
	);
};

export { SandboxMatchModal };
